import React from 'react';
import { Box, Typography } from '@mui/material';
import { Forum as ForumIcon, Contacts as ContactsIcon, Group as GroupIcon, Person as PersonIcon } from '@mui/icons-material';

export type MobileTab = 'conversations' | 'contacts' | 'groups' | 'profile';

interface Props {
  activeTab: MobileTab;
  unreadTotal: number;
  pendingCount: number;
  onSelect: (tab: MobileTab) => void;
}

const TABS: { key: MobileTab; label: string; icon: React.ReactElement }[] = [
  { key: 'conversations', label: '会话', icon: <ForumIcon /> },
  { key: 'contacts', label: '通讯录', icon: <ContactsIcon /> },
  { key: 'groups', label: '群聊', icon: <GroupIcon /> },
  { key: 'profile', label: '我的', icon: <PersonIcon /> },
];

// 底部气泡导航栏(移动端):胶囊容器 + 当前项高亮为气泡
export default function MobileBottomNav({ activeTab, unreadTotal, pendingCount, onSelect }: Props) {
  const badgeFor = (key: MobileTab): number | null => {
    if (key === 'conversations' && unreadTotal > 0) return unreadTotal;
    if (key === 'contacts' && pendingCount > 0) return pendingCount;
    return null;
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1200,
        display: 'flex',
        justifyContent: 'center',
        pb: 'calc(env(safe-area-inset-bottom, 0px) + 10px)',
        pt: 1,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.75,
          borderRadius: 999,
          bgcolor: 'rgba(255,255,255,0.92)',
          boxShadow: '0 6px 24px rgba(0,0,0,0.14)',
          backdropFilter: 'blur(8px)',
        }}
      >
        {TABS.map((t) => {
          const active = activeTab === t.key;
          const badge = badgeFor(t.key);
          return (
            <Box
              key={t.key}
              onClick={() => onSelect(t.key)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 62,
                py: 0.6,
                px: 1,
                borderRadius: 999,
                cursor: 'pointer',
                color: active ? '#fff' : 'text.secondary',
                bgcolor: active ? 'primary.main' : 'transparent',
                transition: 'all .2s',
                '&:active': { opacity: 0.8 },
              }}
            >
              {/* 气泡(未读/申请数)固定在图标右上角 */}
              <Box sx={{ position: 'relative', fontSize: 22, lineHeight: 1 }}>
                {t.icon}
                {badge !== null && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -10,
                      minWidth: 16,
                      height: 16,
                      px: 0.5,
                      borderRadius: 8,
                      bgcolor: 'error.main',
                      color: '#fff',
                      fontSize: 10,
                      lineHeight: '16px',
                      textAlign: 'center',
                      fontWeight: 600,
                      boxShadow: '0 0 0 1px #fff',
                    }}
                  >
                    {badge > 99 ? '99+' : badge}
                  </Box>
                )}
              </Box>
              <Typography sx={{ fontSize: 11, mt: 0.3, lineHeight: 1, fontWeight: active ? 600 : 400 }}>
                {t.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
